import React from 'react';
import {
    achievementFamily,
    achievementMotif,
    achievementNumericValue,
    achievementTier,
} from './achievementVisual';
import {
    v2AchievementAssetUrl,
    v2AchievementFamilyAssetUrl,
} from './achievementArtMapV2';

/**
 * V2-only achievement artwork. Does not touch Assets/achievements (V1).
 */

function goldForTier(tier) {
    if (tier >= 4) return '#f3d789';
    if (tier >= 3) return '#efd078';
    if (tier >= 2) return '#d4b45a';
    return '#c9a227';
}

function MedallionShell({ uid, gold, rich, children }) {
    return (
        <svg viewBox="0 0 120 120" width="100%" height="100%" aria-hidden>
            <defs>
                <radialGradient id={`${uid}-face`} cx="38%" cy="28%" r="72%">
                    <stop offset="0%" stopColor="#2a3344" />
                    <stop offset="100%" stopColor="#12161f" />
                </radialGradient>
                <linearGradient id={`${uid}-rim`} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#f6e4a8" />
                    <stop offset="45%" stopColor={gold} />
                    <stop offset="100%" stopColor="#8a7018" />
                </linearGradient>
            </defs>
            <circle cx="60" cy="60" r="56" fill={`url(#${uid}-rim)`} />
            <circle
                cx="60"
                cy="60"
                r={rich ? 50 : 51.5}
                fill={`url(#${uid}-face)`}
                stroke="rgba(255,255,255,0.08)"
                strokeWidth="1"
            />
            {children}
        </svg>
    );
}

function SymbolBreakthrough() {
    return (
        <g fill="none" stroke="#efd078" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M32 78 L52 52 L64 64 L88 38" />
            <path d="M28 86 H92" opacity="0.55" />
            <circle cx="88" cy="38" r="5" fill="#efd078" stroke="none" />
        </g>
    );
}

function SymbolPath() {
    return (
        <g fill="none" stroke="#efd078" strokeWidth="1.8" strokeLinecap="round">
            <path d="M40 86 C46 70 52 66 60 54 C68 42 70 34 74 28" />
            <path d="M34 86 H50" opacity="0.5" />
            <circle cx="74" cy="28" r="4.5" fill="#efd078" stroke="none" />
        </g>
    );
}

function SymbolGoal({ value }) {
    const n = Number(value);
    return (
        <g>
            <g fill="none" stroke="#efd078" strokeWidth="1.8" strokeLinecap="round">
                <path d="M44 82 V40" />
                <path d="M44 40 H72 L66 50 H44" fill="rgba(239,208,120,0.18)" />
            </g>
            {Number.isFinite(n) && n > 0 ? (
                <text
                    x="78"
                    y="38"
                    textAnchor="middle"
                    fill="#fff"
                    fontSize="16"
                    fontFamily="HarmoniaBold, Harmonia, sans-serif"
                    fontWeight="700"
                >
                    {n}
                </text>
            ) : null}
        </g>
    );
}

function SymbolLevel({ motif }) {
    const bars = motif === 'level3' ? 3 : motif === 'level2' ? 2 : 1;
    return (
        <g>
            {[0, 1, 2].map((i) => (
                <rect
                    key={i}
                    x={42 + i * 12}
                    y={70 - i * 12}
                    width="10"
                    height={16 + i * 12}
                    rx="2"
                    fill={i < bars ? '#efd078' : 'rgba(255,255,255,0.12)'}
                />
            ))}
        </g>
    );
}

function SymbolSpecial({ motif }) {
    const label = motif === 'special777' ? '777' : motif === 'streak365' ? '365' : '77';
    return (
        <g>
            <circle cx="60" cy="52" r="16" fill="none" stroke="#efd078" strokeWidth="1.6" />
            <text
                x="60"
                y="58"
                textAnchor="middle"
                fill="#fff"
                fontSize={label.length > 2 ? '13' : '16'}
                fontFamily="HarmoniaBold, Harmonia, sans-serif"
                fontWeight="700"
            >
                {label}
            </text>
            <path
                d="M40 78 L60 66 L80 78"
                fill="none"
                stroke="#efd078"
                strokeWidth="1.6"
                strokeLinejoin="round"
            />
        </g>
    );
}

function SymbolMystery() {
    return (
        <text
            x="60"
            y="66"
            textAnchor="middle"
            fill="#efd078"
            fontSize="36"
            fontFamily="HarmoniaBold, Harmonia, sans-serif"
        >
            ?
        </text>
    );
}

function SymbolGeneric() {
    return (
        <g fill="none" stroke="#efd078" strokeWidth="1.7" strokeLinejoin="round">
            <path d="M60 34 L68 52 H88 L72 64 L78 84 L60 73 L42 84 L48 64 L32 52 H52 Z" />
        </g>
    );
}

function BadgeMark({ uid, motif, value, gold, tier }) {
    let symbol = <SymbolGeneric />;
    if (motif === 'breakthrough') symbol = <SymbolBreakthrough />;
    else if (motif === 'path') symbol = <SymbolPath />;
    else if (motif === 'goal') symbol = <SymbolGoal value={value} />;
    else if (motif === 'level1' || motif === 'level2' || motif === 'level3') {
        symbol = <SymbolLevel motif={motif} />;
    } else if (
        motif === 'special77' ||
        motif === 'special777' ||
        motif === 'streak77' ||
        motif === 'streak365'
    ) {
        symbol = <SymbolSpecial motif={motif} />;
    } else if (motif === 'mystery') symbol = <SymbolMystery />;

    return (
        <MedallionShell uid={uid} gold={gold} rich={tier >= 3}>
            {symbol}
        </MedallionShell>
    );
}

function MilestoneMark({ uid, value, gold, tier }) {
    const label = value === 1 ? 'ACTION STEP' : 'ACTION STEPS';
    const display = Number(value).toLocaleString('en-US');
    const fontSize = display.length >= 4 ? 22 : display.length >= 3 ? 26 : 30;
    return (
        <MedallionShell uid={uid} gold={gold} rich={tier >= 2}>
            <path
                d="M28 84 L48 58 L60 70 L92 40"
                fill="none"
                stroke="rgba(239,208,120,0.55)"
                strokeWidth={1.4 + Math.min(1.2, tier * 0.35)}
                strokeLinejoin="round"
                strokeLinecap="round"
            />
            <path
                d="M24 88 H96"
                stroke="rgba(255,255,255,0.18)"
                strokeWidth="1.2"
            />
            <text
                x="60"
                y="48"
                textAnchor="middle"
                fill="#fff"
                fontSize={fontSize}
                fontFamily="HarmoniaBold, Harmonia, sans-serif"
                fontWeight="700"
            >
                {display}
            </text>
            <text
                x="60"
                y="102"
                textAnchor="middle"
                fill="rgba(239,208,120,0.85)"
                fontSize="7"
                letterSpacing="1.4"
                fontFamily="Harmonia, sans-serif"
                fontWeight="700"
            >
                {label}
            </text>
        </MedallionShell>
    );
}

function StreakMark({ uid, value, gold, tier }) {
    const display = Number(value).toLocaleString('en-US');
    const fontSize = display.length >= 3 ? 22 : 28;
    return (
        <MedallionShell uid={uid} gold={gold} rich={tier >= 2}>
            <path
                d="M60 26 C54 36 51 42 51 50 C51 59 55 66 60 66 C65 66 69 59 69 50 C69 45 67 40 64 35 C66 43 63 46 60 46 C58 41 58 32 60 26 Z"
                fill="rgba(239,208,120,0.16)"
                stroke="#efd078"
                strokeWidth="1.5"
                strokeLinejoin="round"
            />
            <text
                x="60"
                y="88"
                textAnchor="middle"
                fill="#fff"
                fontSize={fontSize}
                fontFamily="HarmoniaBold, Harmonia, sans-serif"
                fontWeight="700"
            >
                {display}
            </text>
            <text
                x="60"
                y="100"
                textAnchor="middle"
                fill="rgba(239,208,120,0.82)"
                fontSize="7.5"
                letterSpacing="1.6"
                fontFamily="Harmonia, sans-serif"
                fontWeight="700"
            >
                DAYS
            </text>
        </MedallionShell>
    );
}

/**
 * Premium V2 medallion for Profile achievements.
 * Falls back to null so AchievementMark can use legacy ImageIcon.
 */
export default function AchievementArtV2({
    item,
    tabHint,
    goalsCompleted = 0,
    instanceId = 'a',
}) {
    const family = achievementFamily(item, tabHint);
    const value = achievementNumericValue(
        item,
        family === 'badge' ? goalsCompleted : 0
    );
    const motif = achievementMotif(item, family);
    const tier = achievementTier(family, motif, value);
    const gold = goldForTier(tier);
    const uid = `v2a-${instanceId}-${item?.id ?? motif}-${family}`;

    const uniqueUrl = v2AchievementAssetUrl(motif);
    if (uniqueUrl) {
        return (
            <img
                className={`dv2-am__file dv2-am__file--${motif}`}
                src={uniqueUrl}
                alt=""
                draggable={false}
                loading="lazy"
                decoding="async"
            />
        );
    }

    const seriesUrl = v2AchievementFamilyAssetUrl(family, tier);
    if (seriesUrl && (family === 'milestone' || family === 'streak')) {
        const display = Number(value || 1).toLocaleString('en-US');
        const digits = String(value || 1).replace(/[^\d]/g, '').length;
        return (
            <span className="dv2-am__stack">
                <img
                    className={`dv2-am__file dv2-am__file--${family}`}
                    src={seriesUrl}
                    alt=""
                    draggable={false}
                    loading="lazy"
                    decoding="async"
                />
                <span
                    className={`dv2-am__value dv2-am__value--${Math.min(4, digits)}`}
                >
                    {display}
                </span>
            </span>
        );
    }

    if (family === 'milestone') {
        return (
            <MilestoneMark uid={uid} value={value || 1} gold={gold} tier={tier} />
        );
    }
    if (family === 'streak') {
        return <StreakMark uid={uid} value={value || 1} gold={gold} tier={tier} />;
    }
    return (
        <BadgeMark
            uid={uid}
            motif={motif}
            value={value}
            gold={gold}
            tier={tier}
        />
    );
}
